import React from 'react';
import { Lock, ArrowRight } from 'lucide-react';
import { usePlan } from '../context/PlanContext';

var PLAN_NAMES = { free: 'Free', basic: 'Basic', standard: 'Standard', pro: 'Pro' };

function goToBilling() {
  // Dashboard listens for this event; fallback to direct nav if not mounted.
  window.dispatchEvent(new CustomEvent('navigate-billing'));
  if (!window.location.pathname.includes('/dashboard') && !window.location.pathname.includes('/billing')) {
    window.location.href = '/billing';
  }
}

export function PlanGate({ feature, minPlan, children, label }) {
  var { canAccess, planAtLeast, plan } = usePlan();

  var hasAccess = minPlan ? planAtLeast(minPlan) : canAccess(feature);

  if (hasAccess) return children;

  var requiredPlan = minPlan || 'basic';

  return (
    <div className="relative" data-testid={'plan-gate-' + (feature || minPlan)}>
      <div className="pointer-events-none opacity-30 blur-[1px] select-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className="bg-gradient-to-br from-[#2D2560] to-[#5A4E9E] border border-white/15 rounded-2xl p-6 max-w-sm text-center shadow-2xl text-white">
          <div className="w-12 h-12 rounded-full bg-white/15 border border-white/25 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-semibold text-sm mb-1 text-white">
            {label || 'Feature Locked'}
          </h3>
          <p className="text-xs text-white/80 mb-4">
            Upgrade to <span className="font-semibold text-white">{PLAN_NAMES[requiredPlan]}</span> or higher to unlock this feature.
          </p>
          <button onClick={goToBilling}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-[#2D2560] text-xs font-semibold hover:bg-white/90 transition-colors mb-3"
            data-testid={'plan-gate-upgrade-' + (feature || minPlan)}>
            Upgrade <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <p className="text-xs text-white/60">Current plan: {PLAN_NAMES[plan]}</p>
        </div>
      </div>
    </div>
  );
}

export function PlanBadge({ feature, minPlan, inline }) {
  var { canAccess, planAtLeast } = usePlan();
  var hasAccess = minPlan ? planAtLeast(minPlan) : canAccess(feature);
  if (hasAccess) return null;
  if (inline) {
    return <Lock className="w-3 h-3 text-white/30 ml-1 inline" />;
  }
  return (
    <span className="ml-auto px-1.5 py-0.5 rounded text-[8px] font-bold uppercase bg-white/5 text-white/30 border border-white/10">
      <Lock className="w-2.5 h-2.5 inline mr-0.5" />PRO
    </span>
  );
}

export function UsageMeter({ counterType, label }) {
  var { getUsage } = usePlan();
  var usage = getUsage(counterType);
  if (!usage || usage.unlimited) return null;
  if (usage.limit <= 0) return null;
  var pct = Math.min((usage.used / usage.limit) * 100, 100);
  var atLimit = usage.remaining <= 0;

  return (
    <div className="text-xs" data-testid={'usage-' + counterType}>
      <div className="flex justify-between mb-1">
        <span className="text-white/40">{label}</span>
        <span className={atLimit ? 'text-[#EF4444] font-medium' : 'text-white/50'}>
          {usage.used}/{usage.limit}
        </span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{
          width: pct + '%',
          background: atLimit ? '#EF4444' : pct > 75 ? '#EAB308' : 'linear-gradient(90deg, #2D2560, #5A4E9E)',
        }}></div>
      </div>
    </div>
  );
}
